"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useRef, useEffect } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { FormDefinition, FormField, FormFieldWidth, FormPage } from "../../features/forms/types";
import { useHubspotForm } from "../../features/forms/hooks/useHubspotForm";
import { FieldRenderer } from "./FieldRenderer";
import { FriendlyCaptchaWidget } from "./FriendlyCaptchaWidget";
import { Button } from "@/components/ui/buttons";
import { Checkbox } from "@/components/ui/form/Checkbox";
import { pushToDataLayer } from "@/lib/analytics/pushToDataLayer";
import { useHubspotFormTool, useContactPersonTool } from "@/features/webmcp";
import { Heading } from "../ui/Heading";
import { Wysiwyg } from "../ui/Wysiwyg";
import { getFormUserData } from "@/lib/analytics/hashUserData";
import { useTranslations } from "@/lib/i18n/useTranslations";
const formVariants = cva("flex flex-col gap-lg w-full", {
  variants: {
    variant: {
      light: "text-off-white",
      dark: "text-black"
    },
    columns: {
      "1": "",
      "2": ""
    }
  },
  defaultVariants: {
    variant: "light",
    columns: "1"
  }
});
interface HubspotFormProps extends VariantProps<typeof formVariants> {
  formDefinition: FormDefinition;
  onSuccess?: () => void;
  transferPersonio?: boolean;
  jobPositionId?: number;
  className?: string;
  /** Set on person pages to expose the `contact_person` tool instead of the generic form tool. */
  contactPerson?: {
    name: string;
    jobtitle?: string;
  };
  /** CMS-authored step grouping. Empty/absent → single-page form. */
  pages?: FormPage[];
}

/** A field plus its CMS-pinned width, if the editor set one. */
type StepField = FormField & {
  cmsWidth?: FormFieldWidth | null;
};

/**
 * HubspotForm component
 * Manages form state and handles submission via useHubspotForm hook.
 */
export const HubspotForm: React.FC<HubspotFormProps> = t0 => {
  const $ = _c(107);
  const {
    formDefinition,
    onSuccess,
    variant,
    columns,
    transferPersonio,
    jobPositionId,
    className,
    contactPerson,
    pages
  } = t0;
  const {
    fields,
    submitButtonLabel,
    richTexts
  } = formDefinition;
  const {
    forms: t
  } = useTranslations();
  let t1;
  if ($[0] !== fields) {
    t1 = () => {
      const initial = {};
      fields.forEach(f => {
        if (f.defaultValue) {
          initial[f.name] = f.defaultValue;
        }
      });
      return initial;
    };
    $[0] = fields;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [formData, setFormData] = useState(t1);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [started, setStarted] = useState(false);
  let t2;
  if ($[2] !== formDefinition || $[3] !== jobPositionId || $[4] !== transferPersonio) {
    t2 = {
      formDefinition,
      transferPersonio,
      jobPositionId
    };
    $[2] = formDefinition;
    $[3] = jobPositionId;
    $[4] = transferPersonio;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    submitForm,
    loading,
    success,
    error
  } = useHubspotForm(t2);
  let t3;
  if ($[6] !== formData || $[7] !== formDefinition || $[8] !== submitForm) {
    t3 = async data => {
      const res = await submitForm(data);
      if (res.success) {
        pushToDataLayer({
          event: "form_submission",
          id: formDefinition.id,
          name: formDefinition.name,
          user_data: await getFormUserData(formData, formDefinition)
        });
      }
      return res;
    };
    $[6] = formData;
    $[7] = formDefinition;
    $[8] = submitForm;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const submitViaTool = t3;
  const isPersonContact = !!contactPerson;
  const t4 = !isPersonContact;
  let t5;
  if ($[10] !== formDefinition || $[11] !== submitViaTool || $[12] !== t4) {
    t5 = {
      formDefinition,
      submit: submitViaTool,
      enabled: t4
    };
    $[10] = formDefinition;
    $[11] = submitViaTool;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  useHubspotFormTool(t5);
  let t6;
  if ($[14] !== contactPerson || $[15] !== formDefinition || $[16] !== isPersonContact || $[17] !== submitViaTool) {
    t6 = {
      person: contactPerson,
      formDefinition,
      submit: submitViaTool,
      enabled: isPersonContact
    };
    $[14] = contactPerson;
    $[15] = formDefinition;
    $[16] = isPersonContact;
    $[17] = submitViaTool;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  useContactPersonTool(t6);
  let t7;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = (name, value) => {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    };
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const handleFieldChange = t7;
  let t8;
  if ($[20] !== formData) {
    t8 = field => {
      if (!field.logic?.displayCondition) {
        return true;
      }
      const {
        controllingField,
        operator,
        values
      } = field.logic.displayCondition;
      const controllingValue = String(formData[controllingField] ?? "");
      switch (operator) {
        case "SET_ANY":
        case "EQUAL":
          {
            return values.includes(controllingValue);
          }
        case "NOT_SET_ANY":
          {
            return !values.includes(controllingValue);
          }
        default:
          {
            return true;
          }
      }
    };
    $[20] = formData;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const visibleFields = fields.filter(t8);
  let t9;
  bb0: {
    if (!Array.isArray(pages) || !pages.length) {
      t9 = [{
        title: null,
        fields: visibleFields
      }];
      break bb0;
    }
    const byName = new Map(visibleFields.map(_temp));
    const assigned = new Set();
    const grouped = pages.map(page => {
      const stepFields = [];
      const entries = Array.isArray(page?.fields) ? page.fields : [];
      for (const entry of entries) {
        const name_0 = typeof entry === "string" ? entry : entry?.name;
        const cmsWidth = typeof entry === "string" ? null : entry?.width;
        const f_1 = name_0 ? byName.get(name_0) : undefined;
        if (f_1 && !assigned.has(name_0)) {
          stepFields.push(cmsWidth ? {
            ...f_1,
            cmsWidth
          } : f_1);
          assigned.add(name_0);
        }
      }
      return {
        title: page?.title ?? null,
        fields: stepFields
      };
    });
    const leftover = visibleFields.filter(f_2 => !assigned.has(f_2.name));
    if (leftover.length) {
      grouped[grouped.length - 1].fields.push(...leftover);
    }
    const nonEmpty = grouped.filter(_temp2);
    t9 = nonEmpty.length ? nonEmpty : [{
      title: null,
      fields: visibleFields
    }];
  }
  const steps = t9;
  const richTextBefore = new Map();
  const trailingRichText = [];
  for (const {
    html,
    beforeField
  } of richTexts ?? []) {
    if (beforeField === null) {
      trailingRichText.push(html);
    } else {
      const blocks = richTextBefore.get(beforeField) ?? [];
      blocks.push(html);
      richTextBefore.set(beforeField, blocks);
    }
  }
  let t10;
  if ($[22] !== columns || $[23] !== variant) {
    t10 = (html_0, key) => <div key={key} data-component="text-block-content" className={`[&_h2]:headline-3 ${variant === "dark" ? "childLinkDark" : "childLinkLight"} ${columns === "2" ? "col-span-full" : ""}`}><Wysiwyg html={html_0} mode="body" /></div>;
    $[22] = columns;
    $[23] = variant;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const renderRichText = t10;
  const [step, setStep] = useState(0);
  const formRef = useRef(null);
  const currentStep = Math.min(step, steps.length - 1);
  const isMultiStep = steps.length > 1;
  const isLastStep = currentStep >= steps.length - 1;
  const currentFields = steps[currentStep].fields;
  const stepHeaderRef = useRef(null);
  const stepChangedByNavRef = useRef(false);
  let t11;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      if (!stepChangedByNavRef.current) {
        return;
      }
      stepChangedByNavRef.current = false;
      stepHeaderRef.current?.focus();
    };
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== currentStep) {
    t12 = [currentStep];
    $[26] = currentStep;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  useEffect(t11, t12);
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      stepChangedByNavRef.current = true;
      setStep(_temp3);
    };
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const goBack = t13;
  const goNext = () => {
    if (formRef.current && !formRef.current.reportValidity()) {
      return;
    }
    stepChangedByNavRef.current = true;
    setStep(s_0 => Math.min(s_0 + 1, steps.length - 1));
  };
  let t14;
  if ($[29] !== captchaToken || $[30] !== formData || $[31] !== formDefinition || $[32] !== goNext || $[33] !== isLastStep || $[34] !== onSuccess || $[35] !== submitForm) {
    t14 = async e => {
      e.preventDefault();
      if (!isLastStep) {
        goNext();
        return;
      }
      const token = captchaToken ?? undefined;
      setCaptchaToken(null);
      const result = await submitForm(formData, token);
      if (result.success) {
        pushToDataLayer({
          event: "form_submission",
          id: formDefinition.id,
          name: formDefinition.name,
          user_data: await getFormUserData(formData, formDefinition)
        });
        onSuccess?.();
      }
    };
    $[29] = captchaToken;
    $[30] = formData;
    $[31] = formDefinition;
    $[32] = goNext;
    $[33] = isLastStep;
    $[34] = onSuccess;
    $[35] = submitForm;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const handleSubmit = t14;
  if (success) {
    if (formDefinition.successMessage?.type === "thank_you") {
      const t15 = `prose max-w-none ${variant === "dark" ? "text-black" : "text-off-white"}`;
      let t16;
      if ($[37] !== formDefinition.successMessage.value) {
        t16 = {
          __html: formDefinition.successMessage.value
        };
        $[37] = formDefinition.successMessage.value;
        $[38] = t16;
      } else {
        t16 = $[38];
      }
      let t17;
      if ($[39] !== t15 || $[40] !== t16) {
        t17 = <div className="p-xl bg-yellow/10 border-2 border-yellow rounded-2xl"><div className={t15} dangerouslySetInnerHTML={t16} /></div>;
        $[39] = t15;
        $[40] = t16;
        $[41] = t17;
      } else {
        t17 = $[41];
      }
      return t17;
    }
    const t15 = `p-xl bg-yellow/10 border-2 border-yellow rounded-2xl text-center ${variant === "dark" ? "text-black" : "text-off-white"}`;
    let t16;
    if ($[42] !== t.hubspot.success.title) {
      t16 = <Heading style="h3" className="mb-md">{t.hubspot.success.title}</Heading>;
      $[42] = t.hubspot.success.title;
      $[43] = t16;
    } else {
      t16 = $[43];
    }
    let t17;
    if ($[44] !== t.hubspot.success.message) {
      t17 = <p>{t.hubspot.success.message}</p>;
      $[44] = t.hubspot.success.message;
      $[45] = t17;
    } else {
      t17 = $[45];
    }
    let t18;
    if ($[46] !== t15 || $[47] !== t16 || $[48] !== t17) {
      t18 = <div className={t15}>{t16}{t17}</div>;
      $[46] = t15;
      $[47] = t16;
      $[48] = t17;
      $[49] = t18;
    } else {
      t18 = $[49];
    }
    return t18;
  }
  let t15;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setStarted(true);
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  let t16;
  if ($[51] !== className || $[52] !== columns || $[53] !== variant) {
    t16 = formVariants({
      variant,
      columns,
      className
    });
    $[51] = className;
    $[52] = columns;
    $[53] = variant;
    $[54] = t16;
  } else {
    t16 = $[54];
  }
  const t17 = isMultiStep && <div ref={stepHeaderRef} tabIndex={-1} className="flex items-center justify-between gap-md outline-none">{steps[currentStep].title ? <span className="font-medium text-lg">{steps[currentStep].title}</span> : <span />}<span className="text-sm opacity-70" aria-hidden="true">{currentStep + 1} / {steps.length}</span><span className="sr-only">{t.hubspot.step.replace("{current}", String(currentStep + 1)).replace("{total}", String(steps.length))}</span></div>;
  const t18 = columns === "2" ? "grid grid-cols-1 md:grid-cols-2 gap-x-md gap-y-sm" : "flex flex-col gap-x-md gap-y-sm";
  const t19 = currentFields.map((field_0, index) => {
    const isFullWidth = field_0.cmsWidth ? field_0.cmsWidth === "full" : field_0.type === "textarea" || field_0.type === "file";
    const previousField = index > 0 ? currentFields[index - 1] : null;
    const startsNewRow = previousField?.type === "select";
    return <React.Fragment key={field_0.name}>{richTextBefore.get(field_0.name)?.map((html_1, i) => renderRichText(html_1, `${field_0.name}-${i}`))}<div className={columns === "2" ? `${isFullWidth ? "col-span-full" : "col-span-1"} ${startsNewRow ? "md:col-start-1" : ""}` : ""}><FieldRenderer field={field_0} value={formData[field_0.name]} onChange={val => handleFieldChange(field_0.name, val)} disabled={loading} color={variant === "dark" ? "purple" : "dark"} /></div></React.Fragment>;
  });
  const t20 = isLastStep && trailingRichText.map((html_2, i_0) => renderRichText(html_2, `trailing-${i_0}`));
  let t21;
  if ($[55] !== t18 || $[56] !== t19 || $[57] !== t20) {
    t21 = <div className={t18}>{t19}{t20}</div>;
    $[55] = t18;
    $[56] = t19;
    $[57] = t20;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== formDefinition || $[60] !== isLastStep || $[61] !== variant) {
    t22 = isLastStep && formDefinition.legalConsent && <div className="flex flex-col gap-md text-sm mt-md">{formDefinition.legalConsent.communicationConsentText && <div dangerouslySetInnerHTML={{
        __html: formDefinition.legalConsent.communicationConsentText
      }} />}{formDefinition.legalConsent.communicationsCheckboxes.map(cb => <Checkbox key={cb.subscriptionTypeId} id={`consent-${cb.subscriptionTypeId}`} required={cb.required} color={variant === "dark" ? "purple" : "dark"} onChange={e_0 => handleFieldChange(`consent-${cb.subscriptionTypeId}`, (e_0.target as HTMLInputElement).checked)} label={<span className="prose-sm" dangerouslySetInnerHTML={{
        __html: cb.label
      }} />} />)}{formDefinition.legalConsent.consentToProcessText && <div dangerouslySetInnerHTML={{
        __html: formDefinition.legalConsent.consentToProcessText
      }} />}{formDefinition.legalConsent.privacyText && <div className={`text-sm ${variant === "light" ? "text-off-white" : "text-gray-dark"}`} dangerouslySetInnerHTML={{
        __html: formDefinition.legalConsent.privacyText
      }} />}</div>;
    $[59] = formDefinition;
    $[60] = isLastStep;
    $[61] = variant;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== error) {
    t23 = error && <div className="text-red-600 text-sm font-medium">{error}</div>;
    $[63] = error;
    $[64] = t23;
  } else {
    t23 = $[64];
  }
  const t24 = !(started && isLastStep);
  const t25 = `grid transition-[grid-template-rows,opacity] duration-500 ease-out ${started && isLastStep ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`;
  let t26;
  if ($[65] !== started || $[66] !== variant) {
    t26 = started && <FriendlyCaptchaWidget onVerify={setCaptchaToken} theme={variant === "dark" ? "light" : "dark"} />;
    $[65] = started;
    $[66] = variant;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== t26) {
    t27 = <div className="overflow-hidden">{t26}</div>;
    $[68] = t26;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  let t28;
  if ($[70] !== t24 || $[71] !== t25 || $[72] !== t27) {
    t28 = <div inert={t24} className={t25}>{t27}</div>;
    $[70] = t24;
    $[71] = t25;
    $[72] = t27;
    $[73] = t28;
  } else {
    t28 = $[73];
  }
  let t29;
  if ($[74] !== currentStep || $[75] !== goBack || $[76] !== isMultiStep || $[77] !== loading || $[78] !== t || $[79] !== variant) {
    t29 = isMultiStep && currentStep > 0 && <Button type="button" variant="secondary" color={variant === "dark" ? "purple" : "yellow"} disabled={loading} icon="arrow-left" label={t.hubspot.back} onClick={goBack} />;
    $[74] = currentStep;
    $[75] = goBack;
    $[76] = isMultiStep;
    $[77] = loading;
    $[78] = t;
    $[79] = variant;
    $[80] = t29;
  } else {
    t29 = $[80];
  }
  let t30;
  if ($[81] !== goNext || $[82] !== isLastStep || $[83] !== loading || $[84] !== t || $[85] !== variant) {
    t30 = !isLastStep && <Button type="button" variant="primary" color={variant === "dark" ? "purple" : "yellow"} disabled={loading} icon="arrow-right" label={t.hubspot.next} onClick={goNext} />;
    $[81] = goNext;
    $[82] = isLastStep;
    $[83] = loading;
    $[84] = t;
    $[85] = variant;
    $[86] = t30;
  } else {
    t30 = $[86];
  }
  let t31;
  if ($[87] !== isLastStep || $[88] !== loading || $[89] !== submitButtonLabel || $[90] !== t || $[91] !== variant) {
    t31 = isLastStep && <Button type="submit" variant="primary" color={variant === "dark" ? "purple" : "yellow"} disabled={loading} icon="arrow-right" label={loading ? t.hubspot.sending : submitButtonLabel || t.hubspot.submit} />;
    $[87] = isLastStep;
    $[88] = loading;
    $[89] = submitButtonLabel;
    $[90] = t;
    $[91] = variant;
    $[92] = t31;
  } else {
    t31 = $[92];
  }
  let t32;
  if ($[93] !== t29 || $[94] !== t30 || $[95] !== t31) {
    t32 = <div className="mt-md flex items-center gap-md self-start">{t29}{t30}{t31}</div>;
    $[93] = t29;
    $[94] = t30;
    $[95] = t31;
    $[96] = t32;
  } else {
    t32 = $[96];
  }
  let t33;
  if ($[97] !== handleSubmit || $[98] !== t15 || $[99] !== t16 || $[100] !== t17 || $[101] !== t21 || $[102] !== t22 || $[103] !== t23 || $[104] !== t28 || $[105] !== t32) {
    t33 = <form ref={formRef} onSubmit={handleSubmit} onFocusCapture={t15} className={t16}>{t17}{t21}{t22}{t23}{t28}{t32}</form>;
    $[97] = handleSubmit;
    $[98] = t15;
    $[99] = t16;
    $[100] = t17;
    $[101] = t21;
    $[102] = t22;
    $[103] = t23;
    $[104] = t28;
    $[105] = t32;
    $[106] = t33;
  } else {
    t33 = $[106];
  }
  return t33;
};
function _temp(f_0) {
  return [f_0.name, f_0];
}
function _temp2(g) {
  return g.fields.length > 0;
}
function _temp3(s) {
  return Math.max(s - 1, 0);
}